import React, { useState, useRef, useCallback, useMemo } from 'react';
import {
  Paper,
  Typography,
  IconButton,
  Button,
  Chip,
  Card,
  CardContent,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Fab
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  Today,
  ZoomIn,
  ZoomOut,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FilterList,
  Timeline as TimelineIcon,
  Person as PersonIcon,
  Group as GroupIcon,
  SmartToy as AIIcon,
  AccountTree as WorkflowIcon
} from '@mui/icons-material';
import { Task } from '../../services/api/todo.service';

interface TimelineViewProps {
  tasks: Task[];
  onTaskClick?: (taskId: string) => void;
  onTaskCreate?: (taskData: any) => Promise<void>;
  onTaskUpdate?: (taskId: string, updates: any) => Promise<void>;
  onTaskDelete?: (taskId: string) => Promise<void>;
  onTaskReorder?: (tasks: { task_id: string; daily_rank: number; due_date: string }[]) => Promise<void>;
  loading?: boolean;
  startDate?: Date;
  endDate?: Date;
  onDateRangeChange?: (startDate: Date, endDate: Date) => void;
  showAssignedToOthers?: boolean;
  onShowAssignedToOthersChange?: (show: boolean) => void;
  zoomLevel?: 'days' | 'weeks' | 'months';
  onZoomLevelChange?: (level: 'days' | 'weeks' | 'months') => void;
}

// Timeline configuration
const TIMELINE_CONFIG = {
  days: {
    label: 'Days',
    dayWidth: 120,
    showTime: true,
    dateFormat: { weekday: 'short', month: 'short', day: 'numeric' } as Intl.DateTimeFormatOptions
  },
  weeks: {
    label: 'Weeks',
    dayWidth: 80,
    showTime: false,
    dateFormat: { month: 'short', day: 'numeric' } as Intl.DateTimeFormatOptions
  },
  months: {
    label: 'Months',
    dayWidth: 40,
    showTime: false,
    dateFormat: { month: 'short', day: 'numeric' } as Intl.DateTimeFormatOptions
  }
};

// Status and priority colors (same as CalendarView)
const STATUS_COLORS = {
  pending: '#FFA726',
  in_progress: '#42A5F5',
  blocked: '#FF7043',
  completed: '#66BB6A',
  cancelled: '#EF5350',
  in_review: '#AB47BC'
};

const PRIORITY_COLORS = {
  low: '#81C784',
  medium: '#FFB74D',
  high: '#FF8A65',
  urgent: '#E57373'
};

export const TimelineView: React.FC<TimelineViewProps> = ({
  tasks,
  onTaskClick,
  onTaskCreate,
  onTaskUpdate,
  onTaskDelete,
  onTaskReorder,
  loading = false,
  startDate = new Date(),
  endDate = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), // 30 days from now
  onDateRangeChange,
  showAssignedToOthers = false,
  onShowAssignedToOthersChange,
  zoomLevel = 'days',
  onZoomLevelChange
}) => {
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
    task: Task;
  } | null>(null);
  const [draggedTask, setDraggedTask] = useState<Task | null>(null);
  const [dragOverDate, setDragOverDate] = useState<string | null>(null);
  const [quickCreateOpen, setQuickCreateOpen] = useState(false);
  const [quickCreateDate, setQuickCreateDate] = useState<string>('');
  const [quickTaskTitle, setQuickTaskTitle] = useState('');

  const timelineRef = useRef<HTMLDivElement>(null);

  const config = TIMELINE_CONFIG[zoomLevel];

  // Generate date range
  const dateRange = useMemo(() => {
    const dates: Date[] = [];
    const current = new Date(startDate);
    current.setHours(0, 0, 0, 0);

    while (current <= endDate) {
      dates.push(new Date(current));

      if (zoomLevel === 'days') {
        current.setDate(current.getDate() + 1);
      } else if (zoomLevel === 'weeks') {
        current.setDate(current.getDate() + 7);
      } else {
        current.setMonth(current.getMonth() + 1);
      }
    }

    return dates;
  }, [startDate, endDate, zoomLevel]);

  // Group tasks by date and collect unscheduled tasks
  const tasksByDate = useMemo(() => {
    const grouped = new Map<string, Task[]>();

    tasks.forEach(task => {
      if (!task.due_date) {
        // Add unscheduled tasks to a special key
        if (!grouped.has('unscheduled')) {
          grouped.set('unscheduled', []);
        }
        grouped.get('unscheduled')!.push(task);
      } else {
        const taskDate = new Date(task.due_date);
        const dateKey = taskDate.toISOString().split('T')[0];

        // Find the appropriate date slot based on zoom level
        let slotKey = dateKey;
        if (zoomLevel === 'weeks') {
          const weekStart = new Date(taskDate);
          weekStart.setDate(taskDate.getDate() - taskDate.getDay());
          slotKey = weekStart.toISOString().split('T')[0];
        } else if (zoomLevel === 'months') {
          slotKey = `${taskDate.getFullYear()}-${String(taskDate.getMonth() + 1).padStart(2, '0')}-01`;
        }

        if (!grouped.has(slotKey)) {
          grouped.set(slotKey, []);
        }
        grouped.get(slotKey)!.push(task);
      }
    });

    // Sort tasks by daily_rank within each date
    grouped.forEach((tasks) => {
      tasks.sort((a, b) => (a.daily_rank || 0) - (b.daily_rank || 0));
    });

    return grouped;
  }, [tasks, zoomLevel]);

  // Navigation handlers
  const navigateTimeline = useCallback((direction: 'prev' | 'next' | 'today') => {
    if (!onDateRangeChange) return;

    let newStart = new Date(startDate);
    let newEnd = new Date(endDate);
    const rangeInDays = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));

    switch (direction) {
      case 'prev':
        newStart.setDate(startDate.getDate() - rangeInDays);
        newEnd.setDate(endDate.getDate() - rangeInDays);
        break;
      case 'next':
        newStart.setDate(startDate.getDate() + rangeInDays);
        newEnd.setDate(endDate.getDate() + rangeInDays);
        break;
      case 'today':
        const today = new Date();
        newStart = new Date(today);
        newEnd = new Date(today);
        newEnd.setDate(today.getDate() + rangeInDays);
        break;
    }

    onDateRangeChange(newStart, newEnd);
  }, [startDate, endDate, onDateRangeChange]);

  // Zoom handlers
  const handleZoomIn = () => {
    if (zoomLevel === 'months' && onZoomLevelChange) {
      onZoomLevelChange('weeks');
    } else if (zoomLevel === 'weeks' && onZoomLevelChange) {
      onZoomLevelChange('days');
    }
  };

  const handleZoomOut = () => {
    if (zoomLevel === 'days' && onZoomLevelChange) {
      onZoomLevelChange('weeks');
    } else if (zoomLevel === 'weeks' && onZoomLevelChange) {
      onZoomLevelChange('months');
    }
  };

  // Drag and drop handlers
  const handleTaskDragStart = (e: React.DragEvent, task: Task) => {
    setDraggedTask(task);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleTaskDragEnd = () => {
    setDraggedTask(null);
    setDragOverDate(null);
  };

  const handleDateColumnDragOver = (e: React.DragEvent, dateStr: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverDate(dateStr);
  };

  const handleDateColumnDrop = async (e: React.DragEvent, dateStr: string) => {
    e.preventDefault();
    setDragOverDate(null);

    if (!draggedTask || !onTaskUpdate) return;

    // Allow tasks without due dates to be moved - this sets their initial due date
    if (draggedTask.due_date) {
      const originalDateStr = draggedTask.due_date.split('T')[0];
      if (originalDateStr === dateStr) {
        setDraggedTask(null);
        return;
      }
    }

    try {
      await onTaskUpdate(draggedTask.id, {
        due_date: `${dateStr}T${draggedTask.due_date?.split('T')[1] || '23:59:59'}`
      });
    } catch (error) {
      console.error('Error moving task:', error);
    }

    setDraggedTask(null);
  };

  // Context menu handlers
  const handleTaskRightClick = (e: React.MouseEvent, task: Task) => {
    e.preventDefault();
    setContextMenu({
      mouseX: e.clientX - 2,
      mouseY: e.clientY - 4,
      task
    });
  };

  const handleContextMenuClose = () => {
    setContextMenu(null);
  };

  // Quick create task
  const handleQuickCreate = (dateStr: string) => {
    setQuickCreateDate(dateStr);
    setQuickTaskTitle('');
    setQuickCreateOpen(true);
  };

  const handleQuickCreateSubmit = async () => {
    if (!quickTaskTitle.trim() || !onTaskCreate) return;

    try {
      await onTaskCreate({
        title: quickTaskTitle.trim(),
        due_date: `${quickCreateDate}T23:59:59`,
        status: 'pending',
        priority: 'medium'
      });
      setQuickCreateOpen(false);
    } catch (error) {
      console.error('Error creating task:', error);
    }
  };

  // Get assignment icon
  const getAssignmentIcon = (task: Task) => {
    switch (task.assignment_type) {
      case 'group':
        return <GroupIcon fontSize="small" />;
      case 'ai_agent':
        return <AIIcon fontSize="small" />;
      case 'workflow':
        return <WorkflowIcon fontSize="small" />;
      default:
        return <PersonIcon fontSize="small" />;
    }
  };

  // Task card component
  const TaskCard = React.memo(({ task }: { task: Task }) => (
    <Card
      style={{
        marginBottom: 8,
        cursor: 'grab',
        borderLeft: `4px solid ${PRIORITY_COLORS[task.priority]}`,
        backgroundColor: '#fff',
        border: `1px solid ${STATUS_COLORS[task.status]}`
      }}
      draggable
      onDragStart={(e) => handleTaskDragStart(e, task)}
      onDragEnd={handleTaskDragEnd}
      onContextMenu={(e) => handleTaskRightClick(e, task)}
      onClick={() => onTaskClick?.(task.id)}
    >
      <CardContent style={{ padding: 12 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 8 }}>
          <Chip
            size="small"
            label={task.daily_rank}
            style={{
              backgroundColor: PRIORITY_COLORS[task.priority],
              color: 'white',
              minWidth: 24,
              height: 20,
              fontSize: '0.7rem'
            }}
          />
          {getAssignmentIcon(task)}
        </div>

        <Typography
          variant="body2"
          style={{
            fontWeight: 500,
            marginBottom: 4,
            textDecoration: task.status === 'completed' ? 'line-through' : 'none',
            opacity: task.status === 'completed' ? 0.7 : 1
          }}
        >
          {task.title}
        </Typography>

        {task.user_name && (
          <Typography variant="caption" style={{ color: 'rgba(0,0,0,0.6)', display: 'block' }}>
            {task.user_name}
          </Typography>
        )}

        {task.assigned_group_name && (
          <Typography variant="caption" style={{ color: 'rgba(0,0,0,0.6)', display: 'block' }}>
            Group: {task.assigned_group_name}
          </Typography>
        )}

        <Chip
          size="small"
          label={task.status.replace('_', ' ')}
          style={{
            marginTop: 4,
            backgroundColor: STATUS_COLORS[task.status],
            color: 'white',
            textTransform: 'capitalize',
            fontSize: '0.7rem'
          }}
        />
      </CardContent>
    </Card>
  ));

  return (
    <div>
      {/* Timeline Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <Typography variant="h5" component="h2" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <TimelineIcon />
            Timeline View
          </Typography>

          <div style={{ display: 'flex', alignItems: 'center' }}>
            <IconButton onClick={() => navigateTimeline('prev')}>
              <ChevronLeft />
            </IconButton>
            <Button onClick={() => navigateTimeline('today')} startIcon={<Today />}>
              Today
            </Button>
            <IconButton onClick={() => navigateTimeline('next')}>
              <ChevronRight />
            </IconButton>
          </div>

          <Typography variant="body2" style={{ color: 'rgba(0,0,0,0.6)' }}>
            {startDate.toLocaleDateString()} - {endDate.toLocaleDateString()}
          </Typography>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          {/* Zoom Controls */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <IconButton
              onClick={handleZoomOut}
              disabled={zoomLevel === 'months'}
              size="small"
            >
              <ZoomOut />
            </IconButton>
            <Typography variant="caption" style={{ minWidth: 60, textAlign: 'center' }}>
              {config.label}
            </Typography>
            <IconButton
              onClick={handleZoomIn}
              disabled={zoomLevel === 'days'}
              size="small"
            >
              <ZoomIn />
            </IconButton>
          </div>

          {/* Show Others Toggle */}
          {onShowAssignedToOthersChange && (
            <Button
              variant={showAssignedToOthers ? "contained" : "outlined"}
              size="small"
              onClick={() => onShowAssignedToOthersChange(!showAssignedToOthers)}
              startIcon={<FilterList />}
            >
              Others' Tasks
            </Button>
          )}

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleQuickCreate(new Date().toISOString().split('T')[0])}
          >
            Add Task
          </Button>
        </div>
      </div>

      {/* Timeline Body */}
      <Paper style={{ overflow: 'hidden' }}>
        <div
          ref={timelineRef}
          style={{
            display: 'flex',
            overflowX: 'auto',
            minHeight: 400
          }}
        >
          {dateRange.map((date, index) => {
            const dateStr = date.toISOString().split('T')[0];
            const dayTasks = tasksByDate.get(dateStr) || [];
            const isToday = new Date().toDateString() === date.toDateString();
            const isDragOver = dragOverDate === dateStr;

            return (
              <div
                key={dateStr}
                style={{
                  minWidth: config.dayWidth,
                  width: config.dayWidth,
                  borderRight: '1px solid rgba(0,0,0,0.12)',
                  backgroundColor: isToday ? '#e3f2fd' : isDragOver ? 'rgba(0,0,0,0.04)' : 'transparent'
                }}
                onDragOver={(e) => handleDateColumnDragOver(e, dateStr)}
                onDragLeave={() => setDragOverDate(null)}
                onDrop={(e) => handleDateColumnDrop(e, dateStr)}
                onDoubleClick={() => handleQuickCreate(dateStr)}
              >
                {/* Date Header */}
                <div
                  style={{
                    padding: 8,
                    textAlign: 'center',
                    borderBottom: '1px solid rgba(0,0,0,0.12)',
                    backgroundColor: isToday ? '#1976d2' : '#fafafa',
                    color: isToday ? 'white' : 'rgba(0,0,0,0.87)',
                    position: 'sticky',
                    top: 0,
                    zIndex: 1
                  }}
                >
                  <Typography
                    variant="caption"
                    style={{ display: 'block', fontWeight: isToday ? 'bold' : 'normal' }}
                  >
                    {date.toLocaleDateString('en-US', config.dateFormat)}
                  </Typography>
                  {dayTasks.length > 0 && (
                    <Chip
                      size="small"
                      label={dayTasks.length}
                      style={{
                        height: 16,
                        fontSize: '0.7rem',
                        backgroundColor: isToday ? 'rgba(255,255,255,0.2)' : '#1976d2',
                        color: 'white'
                      }}
                    />
                  )}
                </div>

                {/* Tasks Column */}
                <div style={{ padding: 8 }}>
                  {dayTasks.map(task => (
                    <TaskCard key={task.id} task={task} />
                  ))}

                  {dayTasks.length === 0 && (
                    <div
                      style={{
                        minHeight: 60,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'rgba(0,0,0,0.6)',
                        cursor: 'pointer',
                        borderRadius: 4
                      }}
                      onClick={() => handleQuickCreate(dateStr)}
                    >
                      <AddIcon fontSize="small" />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Unscheduled Tasks Section */}
        {tasksByDate.has('unscheduled') && (
          <div style={{ marginTop: 24, padding: 16, backgroundColor: '#fafafa', borderRadius: 4 }}>
            <Typography variant="h6" style={{ color: 'rgba(0,0,0,0.87)', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
              <TimelineIcon />
              Unscheduled Tasks
            </Typography>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
              {tasksByDate.get('unscheduled')?.map(task => (
                <TaskCard key={task.id} task={task} />
              ))}
            </div>
          </div>
        )}
      </Paper>

      {/* Floating Action Button */}
      <Fab
        color="primary"
        onClick={() => handleQuickCreate(new Date().toISOString().split('T')[0])}
        style={{
          position: 'fixed',
          bottom: 24,
          right: 24
        }}
      >
        <AddIcon />
      </Fab>

      {/* Quick Create Dialog */}
      <Dialog
        open={quickCreateOpen}
        onClose={() => setQuickCreateOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create Task for {new Date(quickCreateDate).toLocaleDateString()}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Task Title"
            value={quickTaskTitle}
            onChange={(e) => setQuickTaskTitle(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && quickTaskTitle.trim()) {
                handleQuickCreateSubmit();
              }
            }}
            style={{ marginTop: 8 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQuickCreateOpen(false)}>Cancel</Button>
          <Button
            onClick={handleQuickCreateSubmit}
            variant="contained"
            disabled={!quickTaskTitle.trim()}
          >
            Create Task
          </Button>
        </DialogActions>
      </Dialog>

      {/* Context Menu */}
      <Menu
        open={contextMenu !== null}
        onClose={handleContextMenuClose}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        <MenuItem onClick={() => {
          if (contextMenu?.task && onTaskClick) {
            onTaskClick(contextMenu.task.id);
          }
          handleContextMenuClose();
        }}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit Task</ListItemText>
        </MenuItem>

        <MenuItem onClick={() => {
          if (contextMenu?.task && onTaskDelete) {
            onTaskDelete(contextMenu.task.id);
          }
          handleContextMenuClose();
        }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Delete Task</ListItemText>
        </MenuItem>
      </Menu>

    </div>
  );
};
